import polygonClipping           from 'polygon-clipping';
import { generateId }            from '@tokamap/core';
import { lngLatToMercator }      from '@tokamap/core';
import { mercatorToLngLat }      from '@tokamap/core';
import { pointAtPosition }       from '@tokamap/core';
import { ringVertices }          from '@tokamap/core';
import { withoutClosingPoint }   from '@tokamap/core';
import { shapeToLatestRawFeature } from '@tokamap/core';
import type { LatestRawFeature }  from '@tokamap/core';
import type { LngLat }            from '@tokamap/core';
import type { ShapeOpening }      from '@tokamap/core';
import type { Shape }             from '@tokamap/core';
import { AREA_KINDS }             from '@/TokamapEditor/helpers/mergeShapes';
import { outline }                from '@/TokamapEditor/helpers/mergeShapes';
import { closedRing }             from '@/TokamapEditor/helpers/mergeShapes';
import { distanceToRingM }        from '@/TokamapEditor/helpers/mergeShapes';
import { mercatorArea }           from '@/TokamapEditor/helpers/mergeShapes';
import { openingSpan }            from '@/TokamapEditor/helpers/mergeShapes';
import asSingleHistoryEntry       from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import { message }                from '@widesk-ui/hooks/useMessage';

type MercatorPolygon = [number, number][][];

export type ShapeCut = { removeId: string; layer?: number; features: LatestRawFeature[] };

export type SplitPlan =
	| { ok: true; cuts: ShapeCut[]; droppedOpenings: number }
	| { ok: false; reason: string };

const CUT_HALF_WIDTH_M = 0.0005;

const MIN_PIECE_AREA_M2 = 0.01;

const OPENING_TOLERANCE_M = 0.05;

/**
 * Prépare la découpe des formes par une ligne : chaque forme que la ligne traverse de part en part
 * devient autant de formes que de morceaux, qui gardent tout de l'original — style, thème, calque,
 * point d'intérêt, murs. Rien n'est appliqué ici.
 */
export function planSplit(shapes: Shape[], line: LngLat[]): SplitPlan {
	if (line.length < 2) return { ok: false, reason: 'Tracez une ligne de coupe d\'au moins deux points.' };

	const cutter = cutterPolygons(line.map(point => lngLatToMercator(point)));
	const cuts: ShapeCut[] = [];
	let droppedOpenings = 0;

	for (const shape of cuttableShapes(shapes, line)) {
		const pieces = splitPolygon(shape, cutter);
		if (pieces.length < 2) continue;

		const { openings, dropped } = distributeOpenings(shape, pieces);
		droppedOpenings += dropped;

		cuts.push({
			removeId: shape.id,
			layer: shape.layer,
			features: pieces.map(([outer, ...holes], index) => shapeToLatestRawFeature({
				...shape,
				id: generateId(),
				shapeKind: 'polygon',
				coordinates: outer!,
				holes: holes.length ? holes : undefined,
				openings: openings[index]!.length ? openings[index] : undefined,
			})),
		});
	}

	if (!cuts.length) {
		return { ok: false, reason: 'La ligne ne traverse aucune forme de part en part : faites-la commencer et finir hors de la forme à découper.' };
	}

	return { ok: true, cuts, droppedOpenings };
}

/** Les morceaux qu'obtiendrait la coupe, pour la prévisualiser : rien n'est préparé pour être appliqué. */
export function previewSplit(shapes: Shape[], line: LngLat[]): LngLat[][][] {
	if (line.length < 2) return [];

	const cutter = cutterPolygons(line.map(point => lngLatToMercator(point)));

	return cuttableShapes(shapes, line).flatMap(shape => {
		const pieces = splitPolygon(shape, cutter);

		return pieces.length < 2 ? [] : pieces;
	});
}

function cuttableShapes(shapes: Shape[], line: LngLat[]): Shape[] {
	const lineBounds = bounds(line);

	return shapes.filter(shape => AREA_KINDS.includes(shape.shapeKind) && overlaps(bounds(outline(shape)), lineBounds));
}

function bounds(points: LngLat[]): [number, number, number, number] {
	const xs = points.map(([x]) => x);
	const ys = points.map(([, y]) => y);

	return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function overlaps([minX, minY, maxX, maxY]: number[], [otherMinX, otherMinY, otherMaxX, otherMaxY]: number[]): boolean {
	return minX! <= otherMaxX! && otherMinX! <= maxX! && minY! <= otherMaxY! && otherMinY! <= maxY!;
}

/** La ligne épaissie d'un millimètre : retirée de la forme, elle la sépare sans qu'il y manque rien de visible. */
function cutterPolygons(line: [number, number][]): MercatorPolygon[] {
	const polygons: MercatorPolygon[] = [];

	for (let i = 0; i + 1 < line.length; i++) {
		const [ax, ay] = line[i]!;
		const [bx, by] = line[i + 1]!;
		const length = Math.hypot(bx - ax, by - ay);
		if (!length) continue;

		const dx = ((bx - ax) / length) * CUT_HALF_WIDTH_M;
		const dy = ((by - ay) / length) * CUT_HALF_WIDTH_M;

		polygons.push([[
			[ax - dx - dy, ay - dy + dx],
			[bx + dx - dy, by + dy + dx],
			[bx + dx + dy, by + dy - dx],
			[ax - dx + dy, ay - dy - dx],
			[ax - dx - dy, ay - dy + dx],
		]]);
	}

	return polygons;
}

function splitPolygon(shape: Shape, cutter: MercatorPolygon[]): LngLat[][][] {
	if (!cutter.length) return [];

	const polygon = [outline(shape), ...(shape.holes ?? [])].map(ring => closedRing(ring).map(point => lngLatToMercator(point)));

	return polygonClipping.difference(polygon, ...cutter)
		.filter(([outer]) => mercatorArea(outer!) > MIN_PIECE_AREA_M2)
		.map(rings => rings.map(ring => ring.map(point => mercatorToLngLat(point as [number, number]))));
}

/** Chaque ouverture va au morceau dont elle longe encore le mur ; celle que la coupe traverse est perdue. */
function distributeOpenings(shape: Shape, pieces: LngLat[][][]): { openings: ShapeOpening[][]; dropped: number } {
	const openings: ShapeOpening[][] = pieces.map(() => []);
	let dropped = 0;

	for (const opening of shape.openings ?? []) {
		const vertices = ringVertices(shape, opening.ring);
		if (vertices.length < 2) continue;

		const span = ((opening.to - opening.from) % vertices.length + vertices.length) % vertices.length;
		const ends = [opening.from, opening.from + span / 2, opening.to].map(position => pointAtPosition(vertices, position));
		const target = pieces
			.flatMap((rings, piece) => rings.map((ring, index) => ({ piece, index, vertices: withoutClosingPoint(ring) })))
			.find(({ vertices: ring }) => ends.every(point => distanceToRingM(ring, point) <= OPENING_TOLERANCE_M));

		if (!target) {
			dropped++;
			continue;
		}

		openings[target.piece]!.push({ ring: target.index, ...openingSpan(target.vertices, ends) });
	}

	return { openings, dropped };
}

/** Découpe par la ligne les formes données qu'elle traverse, en une seule entrée d'historique. */
export async function splitShapes(shapeIds: string[], line: LngLat[]): Promise<void> {
	const instance = currentEditorStore.get;
	if (!instance) return;

	const shapes = shapeIds
		.map(id => instance.shapeStore.get(id))
		.filter((shape): shape is Shape => !!shape);
	const plan = planSplit(shapes, line);

	if (!plan.ok) return void message.warning(plan.reason);

	await asSingleHistoryEntry(() => {
		instance.deleteShape(plan.cuts.map(cut => cut.removeId));
		plan.cuts.forEach(cut => cut.features.forEach(feature => instance.addShapeFromFeature(feature, { layer: cut.layer })));
	});

	const pieces = plan.cuts.reduce((total, cut) => total + cut.features.length, 0);
	const dropped = plan.droppedOpenings
		? `, ${plan.droppedOpenings} ouverture${plan.droppedOpenings > 1 ? 's' : ''} retirée${plan.droppedOpenings > 1 ? 's' : ''} : la coupe la traversait`
		: '';

	message.success(`${plan.cuts.length > 1 ? `${plan.cuts.length} formes découpées` : 'Forme découpée'} en ${pieces} morceaux${dropped}.`);
}
